/* Lux cart plans engine (spec 2026-07-10). One document-level engine drives
   every [data-lux-cart-plans] root (drawer + cart page can coexist on /cart).
   State single-source = sessionStorage.luxCartPlan ({mode, months}); absent or
   blocked = cash (safety rail 2). The drawer swaps its innerHTML on every cart
   change (cart-drawer.js renderContents), so events are DELEGATED on document
   and a MutationObserver re-applies the stored state after each swap. Math =
   lux-pdp.js engine verbatim (final rule 2026-07-07). */
(() => {
  if (window.luxCartPlans) return;
  window.luxCartPlans = true;
  const MIN_MONTHS = 6, MAX_MONTHS = 12, MIN_MONTHLY = 4000;
  const fmtEUR = (cents) => {
    const whole = Math.round(cents) / 100;
    const opts = Number.isInteger(whole)
      ? { maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return "€" + whole.toLocaleString("el-GR", opts);
  };
  const t = (key, repl) => {
    let s = (window.luxCartPlanStrings && window.luxCartPlanStrings[key]) || "";
    Object.entries(repl || {}).forEach(([k, val]) => { s = s.replace(`{{ ${k} }}`, val); });
    return s;
  };
  /* Write only on real change: repaint runs after every cart mutation and on
     every plan click, and unconditional writes re-announce identical values
     through the aria-live receipts and feed mutation storms on spam clicks. */
  const setText = (el, s) => { if (el && el.textContent !== s) el.textContent = s; };
  const readHint = () => {
    try { return JSON.parse(sessionStorage.getItem("luxCartPlan") || "null"); } catch { return null; }
  };
  const saveHint = (mode, months) => {
    try {
      /* cash-1 = the default state, no hint needed; cash-2/3 (split) and inst
         persist so the aitisi page preselects the same plan. */
      if (mode === "cash" && (!months || months <= 1)) sessionStorage.removeItem("luxCartPlan");
      else sessionStorage.setItem("luxCartPlan", JSON.stringify({ mode, months }));
    } catch { /* storage blocked: stays cash on next paint */ }
  };
  const dataOf = (root) => {
    const el = root.querySelector("script[data-lux-cart-data]");
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch { return null; }
  };
  const planFor = (reg) => {
    const deposit = Math.round(reg * 0.2 / 100) * 100;
    const maxMonths = Math.min(MAX_MONTHS, Math.floor((reg - deposit) / MIN_MONTHLY));
    return { deposit, maxMonths };
  };
  const paint = (root) => {
    const d = dataOf(root);
    if (!d) return;
    const { deposit, maxMonths } = planFor(d.reg);
    const hint = readHint();
    const inst = !!(hint && hint.mode === "inst");
    const months = inst && hint.months >= MIN_MONTHS && hint.months <= maxMonths ? hint.months : maxMonths;
    root.querySelectorAll("[data-lux-cart-mode-input]").forEach((r) => { r.checked = (r.value === "inst") === inst; });
    root.querySelectorAll("[data-lux-cart-months] .lux-plans__pill").forEach((pill) => {
      const input = pill.querySelector("input");
      const val = parseInt(input.value, 10);
      pill.hidden = val > maxMonths;
      const on = val === months;
      input.checked = on;
      pill.classList.toggle("lux-plans__pill--active", on);
    });
    const monthly = Math.floor((d.reg - deposit) / months);
    setText(root.querySelector("[data-lux-cart-teaser]"), fmtEUR(monthly));
    /* Receipt rows (Tsoulfa 2026-07-11): deposit / monthly / total, aligned. */
    setText(root.querySelector("[data-lux-cart-val-deposit]"), fmtEUR(deposit));
    setText(root.querySelector("[data-lux-cart-val-monthly]"), months + " × " + fmtEUR(monthly));
    setText(root.querySelector("[data-lux-cart-val-total]"), fmtEUR(d.reg));
    const apply = root.querySelector("[data-lux-cart-apply]:not([data-lux-cash-apply])");
    if (apply) apply.href = apply.href.split("?")[0] + "?cart=1&months=" + months;
    /* Cash detail (Tsoulfa 2026-07-11, PDP logic): 1 = plain checkout with just
       the total; 2/3 = split cash, payments row + apply CTA (aitisi reads the
       cash hint from sessionStorage, so its link carries no months param). */
    const cashN = hint && hint.mode === "cash" && hint.months >= 2 && hint.months <= 3 ? hint.months : 1;
    root.querySelectorAll("[data-lux-cart-cash] .lux-plans__pill").forEach((pill) => {
      const input = pill.querySelector("input");
      const on = parseInt(input.value, 10) === cashN;
      input.checked = on;
      pill.classList.toggle("lux-plans__pill--active", on);
    });
    const cashPayRow = root.querySelector("[data-lux-cash-row-payments]");
    if (cashPayRow) cashPayRow.hidden = cashN <= 1;
    setText(root.querySelector("[data-lux-cash-val-payments]"), cashN + " × " + fmtEUR(Math.floor(d.cash / cashN)));
    setText(root.querySelector("[data-lux-cash-val-total]"), fmtEUR(d.cash));
    const cashApply = root.querySelector("[data-lux-cash-apply]");
    if (cashApply) cashApply.hidden = cashN <= 1;
  };
  const paintAll = () => document.querySelectorAll("[data-lux-cart-plans]").forEach(paint);
  /* Checkout label quiets to «ή ολοκλήρωσε με μετρητοίς» while δόσεις is
     selected; original innerHTML (incl. the lock icon) is restored on cash.
     Look is CSS-driven either way, so no-JS stays safe with the original label. */
  const syncCheckoutLabels = () => {
    const h = readHint();
    /* Quiet the checkout button for inst AND for cash-split (2/3 payments both
       run through the application; instant checkout would charge the full cash
       amount in one payment). ALWAYS require a live block (final review blocker
       2): if the basket dropped below eligibility the block is gone, and a
       stale hint must not relabel the only checkout CTA. */
    const alive = !!document.querySelector("[data-lux-cart-inst]");
    const inst = !!(h && h.mode === "inst") && alive;
    const cashSplit = !!(h && h.mode === "cash" && h.months >= 2) && alive;
    document.querySelectorAll("#CartDrawer-Checkout, .cart__checkout").forEach((btn) => {
      if (!btn.luxOriginalHtml) btn.luxOriginalHtml = btn.innerHTML;
      if (inst) setText(btn, t("or_cash"));
      else if (cashSplit) setText(btn, t("or_cash_full"));
      else if (btn.innerHTML !== btn.luxOriginalHtml) btn.innerHTML = btn.luxOriginalHtml;
    });
  };
  const repaint = () => { paintAll(); syncCheckoutLabels(); };
  document.addEventListener("change", (e) => {
    const root = e.target.closest("[data-lux-cart-plans]");
    if (!root) return;
    const d = dataOf(root) || { reg: 0 };
    const { maxMonths } = planFor(d.reg);
    if (e.target.matches("[data-lux-cart-mode-input]")) {
      const hint = readHint();
      /* Clamp to this basket's window (final review minor 3): a stale or
         cash-split hint must not leak out-of-range months into GTM/storage. */
      const months = Math.min(maxMonths, Math.max(MIN_MONTHS, (hint && hint.months) || maxMonths));
      /* Switching modes resets cash to the plain 1-payment state; inst keeps
         (clamped) months. Cash split is chosen with its own 2/3 pills after. */
      saveHint(e.target.value, e.target.value === "cash" ? 1 : months);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lux_cart_plan_selected", plan_type: e.target.value, months: e.target.value === "inst" ? months : 1 });
      repaint();
    } else if (e.target.name && e.target.name.indexOf("lux-cart-months-") === 0) {
      const months = parseInt(e.target.value, 10);
      saveHint("inst", months);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lux_cart_plan_selected", plan_type: "inst", months });
      repaint();
    } else if (e.target.name && e.target.name.indexOf("lux-cart-cash-") === 0) {
      const n = parseInt(e.target.value, 10);
      saveHint("cash", n);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lux_cart_plan_selected", plan_type: "cash", months: n });
      repaint();
    }
  });
  document.addEventListener("click", (e) => {
    const apply = e.target.closest("[data-lux-cart-apply]");
    if (!apply) return;
    const root = apply.closest("[data-lux-cart-plans]");
    const d = root && dataOf(root);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "lux_cart_application_click", items: d ? d.count : 0, total: d ? d.reg : 0 });
  });
  /* The drawer (and the cart page js-contents) get innerHTML-swapped on cart
     changes; fresh server HTML is cash-default, so re-apply the stored state.
     Single-flight: both observers can fire several batches per frame during a
     swap; one queued repaint per frame covers all of them. */
  const observed = [];
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; repaint(); });
  };
  const observe = () => {
    [document.querySelector("cart-drawer"), document.getElementById("main-cart-footer")].forEach((el) => {
      if (!el || observed.includes(el)) return;
      observed.push(el);
      new MutationObserver(schedule).observe(el, { childList: true, subtree: true });
    });
  };
  const init = () => { observe(); repaint(); };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
